/**
 * Every Life OS REST call the web app makes (spec §4.5 + the §4.4 connections
 * routes), built on the shared `request` wrapper so the session cookie, the
 * 401 → /login bounce and `ApiError` behave exactly like the rest of the app.
 * Paths, verbs and body shapes here are the CONTRACT spellings; `api.test.ts`
 * pins each one.
 */
import { buildQuery, request } from '../api.js';
import type {
  AgentPolicy,
  AgentRunListRow,
  AgentRunRow,
  ApprovalDecision,
  ApprovalRow,
  ApprovalWithProject,
  AuditRow,
  Belonging,
  BriefKind,
  BriefListRow,
  BriefRow,
  ConnectionsPayload,
  ConnectorsPayload,
  CreateTokenInput,
  InboxCounts,
  InboxItem,
  InboxStatus,
  LifeDocument,
  LifeNote,
  MessageRow,
  Moment,
  MomentInput,
  Obligation,
  ObligationCompletion,
  Org,
  OrgDetail,
  Person,
  PersonDetail,
  PersonListItem,
  PersonPatch,
  ProactiveSettings,
  Project,
  ProjectDetail,
  ProjectFile,
  QuickAddResult,
  SearchResult,
  Service,
  SnoozePreset,
  Sphere,
  TaskInput,
  TaskPatch,
  TaskRow,
  TaskView,
  TimelineItem,
  TodayPayload,
  TokenRecord,
  TriagePatch,
  Trip,
  TripDetail,
  TripItem,
  UpcomingItem,
} from './types.js';

/**
 * Extra fetch options for a write. Deferred commits (toast `defer`) pass
 * `KEEPALIVE`, so a commit flushed while the page unloads still reaches the
 * server. Small bodies only: browsers cap in-flight keepalive bodies at 64 KiB,
 * which is why it is opt-in per call rather than the default.
 */
export type SendOptions = Pick<RequestInit, 'keepalive'>;
export const KEEPALIVE: SendOptions = Object.freeze({ keepalive: true });

const json = (method: string, body: unknown, o?: SendOptions): RequestInit => ({ method, body: JSON.stringify(body), ...o });
const post = (body: unknown = {}, o?: SendOptions): RequestInit => json('POST', body, o);
const put = (body: unknown, o?: SendOptions): RequestInit => json('PUT', body, o);
const del = (o?: SendOptions): RequestInit => ({ method: 'DELETE', ...o });
const enc = encodeURIComponent;

// --- Spheres ------------------------------------------------------------------------

export async function listSpheres(): Promise<Sphere[]> {
  return (await request<{ items: Sphere[] }>('/spheres')).items;
}
export async function createSphere(body: { key: string; name: string; color?: string; sort?: number }): Promise<Sphere> {
  return (await request<{ sphere: Sphere }>('/spheres', post(body))).sphere;
}
export async function updateSphere(
  key: string,
  patch: { name?: string; color?: string; sort?: number; archived?: boolean },
): Promise<Sphere> {
  return (await request<{ sphere: Sphere }>(`/spheres/${enc(key)}`, put(patch))).sphere;
}

// --- People + orgs ----------------------------------------------------------------------

export async function listPeople(
  q: { q?: string; org?: string; birthdayMonth?: number; sphere?: string } = {},
): Promise<PersonListItem[]> {
  return (await request<{ items: PersonListItem[] }>(`/people${buildQuery(q)}`)).items;
}
export function getPerson(id: number | string): Promise<PersonDetail> {
  return request(`/people/${enc(String(id))}`);
}
export async function updatePerson(id: number, patch: PersonPatch): Promise<Person> {
  return (await request<{ person: Person }>(`/people/${id}`, put(patch))).person;
}

// --- Mo's Rule moments --------------------------------------------------------------------

export async function listMoments(): Promise<Moment[]> {
  return (await request<{ items: Moment[] }>('/moments')).items;
}
export async function listPersonMoments(personId: number | string): Promise<Moment[]> {
  return (await request<{ items: Moment[] }>(`/people/${enc(String(personId))}/moments`)).items;
}
export async function addMoment(personId: number, body: MomentInput): Promise<Moment> {
  return (await request<{ moment: Moment }>(`/people/${personId}/moments`, post(body))).moment;
}
export async function updateMoment(personId: number, id: string, patch: Partial<MomentInput>): Promise<Moment> {
  return (await request<{ moment: Moment }>(`/people/${personId}/moments/${enc(id)}`, put(patch))).moment;
}
export function deleteMoment(personId: number, id: string, o?: SendOptions): Promise<unknown> {
  return request(`/people/${personId}/moments/${enc(id)}`, del(o));
}

export async function listOrgs(q: { sphere?: string } = {}): Promise<Org[]> {
  return (await request<{ items: Org[] }>(`/orgs${buildQuery(q)}`)).items;
}
export async function createOrg(body: Partial<Org> & { name: string }): Promise<Org> {
  return (await request<{ org: Org }>('/orgs', post(body))).org;
}
export function getOrg(id: string): Promise<OrgDetail> {
  return request(`/orgs/${enc(id)}`);
}
export async function updateOrg(id: string, patch: Partial<Org>): Promise<Org> {
  return (await request<{ org: Org }>(`/orgs/${enc(id)}`, put(patch))).org;
}

// --- Projects ---------------------------------------------------------------------------

export type ProjectInput = Pick<Project, 'slug' | 'name'> &
  Partial<Pick<Project, 'sphereKey' | 'orgId' | 'kind' | 'status' | 'stagingUrl' | 'prodUrl' | 'hosting' | 'notes' | 'parentId'>>;

export async function listProjects(q: { sphere?: string } = {}): Promise<Project[]> {
  return (await request<{ items: Project[] }>(`/projects${buildQuery(q)}`)).items;
}
export async function createProject(body: ProjectInput): Promise<Project> {
  return (await request<{ project: Project }>('/projects', post(body))).project;
}
export function getProject(id: string): Promise<ProjectDetail> {
  return request(`/projects/${enc(id)}`);
}
export async function updateProject(
  id: string,
  patch: Partial<Omit<ProjectInput, 'slug'>> & { autonomyCeiling?: number },
  o?: SendOptions,
): Promise<Project> {
  return (await request<{ project: Project }>(`/projects/${enc(id)}`, put(patch, o))).project;
}

/** Upload one file into a project, with an optional display name and "what is this" line. */
export async function uploadProjectFile(projectId: string, file: File, meta: { name?: string; about?: string } = {}): Promise<ProjectFile> {
  const form = new FormData();
  // Text fields first: the server reads them before the file part.
  if (meta.name) form.append('name', meta.name);
  if (meta.about) form.append('about', meta.about);
  form.append('file', file);
  return (await request<{ file: ProjectFile }>(`/projects/${enc(projectId)}/files`, { method: 'POST', body: form })).file;
}
export async function updateProjectFile(projectId: string, id: string, patch: { name?: string; about?: string | null }): Promise<ProjectFile> {
  return (await request<{ file: ProjectFile }>(`/projects/${enc(projectId)}/files/${enc(id)}`, put(patch))).file;
}
export function deleteProjectFile(projectId: string, id: string, o?: SendOptions): Promise<unknown> {
  return request(`/projects/${enc(projectId)}/files/${enc(id)}`, del(o));
}

// --- Tasks ----------------------------------------------------------------------------------

export function listTasks(
  q: { view?: TaskView; status?: string; sphere?: string; project?: string; q?: string; limit?: number } = {},
): Promise<{ items: TaskRow[]; total: number }> {
  return request(`/tasks${buildQuery(q)}`);
}
export async function createTask(body: TaskInput): Promise<TaskRow> {
  return (await request<{ task: TaskRow }>('/tasks', post(body))).task;
}
/**
 * `POST /api/capture` (spec 2026-10-13-connections): the server files the words
 * as a task, a calendar event or a note (model-routed, rules as fallback).
 * `task` + `parsed` are present for a task capture, exactly as before.
 */
export type CaptureResult = Partial<QuickAddResult> & {
  kind: 'task' | 'event' | 'note';
  summary: string;
  routed: 'model' | 'rules';
  event?: { id: string; title: string; startsAt: string; endsAt?: string | null; allDay?: boolean; domain?: string; meta?: unknown };
  note?: LifeNote;
};

/** The web quick-add ("Add a task…"): always a task, same parsing as the MCP `capture` tool. */
export function quickAddTask(text: string): Promise<QuickAddResult> {
  return request('/tasks/quick', post({ text }));
}

/** The Capture sheet: the server decides task / event / note. */
export function captureText(text: string): Promise<CaptureResult> {
  return request('/capture', post({ text }));
}

/** Undo a capture: drop the task (repeat:null cancels a series), or delete the event / note it made. */
export function undoCapture(r: Pick<CaptureResult, 'kind' | 'task' | 'event' | 'note'>): Promise<unknown> {
  if (r.kind === 'event' && r.event) return request(`/calendar/${enc(r.event.id)}`, del());
  if (r.kind === 'note' && r.note) return request(`/life-notes/${enc(r.note.id)}`, del());
  if (r.task) return request(`/tasks/${enc(r.task.id)}`, put({ status: 'dropped', repeat: null }));
  return Promise.resolve(null);
}
export async function updateTask(id: string, patch: TaskPatch, o?: SendOptions): Promise<TaskRow> {
  return (await request<{ task: TaskRow }>(`/tasks/${enc(id)}`, put(patch, o))).task;
}
export function completeTask(id: string, o?: SendOptions): Promise<{ task: TaskRow; next: TaskRow | null }> {
  return request(`/tasks/${enc(id)}/complete`, post({}, o));
}
export async function snoozeTask(id: string, until: SnoozePreset | string, o?: SendOptions): Promise<TaskRow> {
  return (await request<{ task: TaskRow }>(`/tasks/${enc(id)}/snooze`, post({ until }, o))).task;
}
export async function setFocus(taskIds: string[]): Promise<TaskRow[]> {
  return (await request<{ items: TaskRow[] }>('/tasks/focus', post({ taskIds }))).items;
}

// --- Obligations, services, documents, belongings -----------------------------------------

export type ObligationInput = Pick<Obligation, 'title' | 'kind' | 'dueOn'> &
  Partial<Pick<Obligation, 'recurrence' | 'amountCents' | 'leadDays' | 'sphereKey' | 'notes'>>;

export async function listObligations(q: { sphere?: string } = {}): Promise<Obligation[]> {
  return (await request<{ items: Obligation[] }>(`/obligations${buildQuery(q)}`)).items;
}
export async function createObligation(body: ObligationInput): Promise<Obligation> {
  return (await request<{ obligation: Obligation }>('/obligations', post(body))).obligation;
}
/** Derived rows (autoKey set) accept only active, leadDays and notes; anything else is a 409. */
export async function updateObligation(
  id: string,
  patch: Partial<ObligationInput> & { active?: boolean },
): Promise<Obligation> {
  return (await request<{ obligation: Obligation }>(`/obligations/${enc(id)}`, put(patch))).obligation;
}
export function completeObligation(
  id: string,
  body: { amountCents?: number; note?: string; dueOn?: string } = {},
  o?: SendOptions,
): Promise<{ obligation: Obligation; completion: ObligationCompletion; alreadyLogged: boolean }> {
  return request(`/obligations/${enc(id)}/complete`, post(body, o));
}

export type ServiceInput = Pick<Service, 'name'> &
  Partial<
    Pick<
      Service,
      'sphereKey' | 'costCents' | 'cadence' | 'renewsOn' | 'credentialRef' | 'status' | 'url' | 'notes' | 'vendorOrgId'
    >
  >;

export async function listServices(q: { sphere?: string } = {}): Promise<Service[]> {
  return (await request<{ items: Service[] }>(`/services${buildQuery(q)}`)).items;
}
export async function createService(body: ServiceInput): Promise<Service> {
  return (await request<{ service: Service }>('/services', post(body))).service;
}
export async function updateService(id: string, patch: Partial<ServiceInput>): Promise<Service> {
  return (await request<{ service: Service }>(`/services/${enc(id)}`, put(patch))).service;
}

export type DocumentInput = Pick<LifeDocument, 'kind' | 'label'> &
  Partial<
    Pick<LifeDocument, 'holder' | 'issuer' | 'numberLast4' | 'issuedOn' | 'expiresOn' | 'perks' | 'sphereKey' | 'notes'>
  >;

export async function listDocuments(q: { sphere?: string } = {}): Promise<LifeDocument[]> {
  return (await request<{ items: LifeDocument[] }>(`/documents${buildQuery(q)}`)).items;
}
export async function createDocument(body: DocumentInput): Promise<LifeDocument> {
  return (await request<{ document: LifeDocument }>('/documents', post(body))).document;
}
export async function updateDocument(id: string, patch: Partial<DocumentInput>): Promise<LifeDocument> {
  return (await request<{ document: LifeDocument }>(`/documents/${enc(id)}`, put(patch))).document;
}

export type BelongingInput = Pick<Belonging, 'name'> &
  Partial<
    Pick<
      Belonging,
      'category' | 'sphereKey' | 'valueCents' | 'purchasedOn' | 'purchasedFrom' | 'warrantyUntil' | 'location' | 'notes'
    >
  >;

export async function listBelongings(q: { sphere?: string } = {}): Promise<Belonging[]> {
  return (await request<{ items: Belonging[] }>(`/belongings${buildQuery(q)}`)).items;
}
export async function createBelonging(body: BelongingInput): Promise<Belonging> {
  return (await request<{ belonging: Belonging }>('/belongings', post(body))).belonging;
}
export async function updateBelonging(id: string, patch: Partial<BelongingInput>): Promise<Belonging> {
  return (await request<{ belonging: Belonging }>(`/belongings/${enc(id)}`, put(patch))).belonging;
}
export async function lendBelonging(
  id: string,
  to: { contactId: number } | { text: string },
  o?: SendOptions,
): Promise<Belonging> {
  return (await request<{ belonging: Belonging }>(`/belongings/${enc(id)}/lend`, post(to, o))).belonging;
}
export async function returnBelonging(id: string, o?: SendOptions): Promise<Belonging> {
  return (await request<{ belonging: Belonging }>(`/belongings/${enc(id)}/return`, post({}, o))).belonging;
}

// --- Trips -------------------------------------------------------------------------------------

export type TripInput = Pick<Trip, 'title'> &
  Partial<Pick<Trip, 'destination' | 'startsOn' | 'endsOn' | 'people' | 'packing' | 'notes' | 'sphereKey'>>;
export type TripItemInput = Pick<TripItem, 'kind' | 'title'> &
  Partial<Pick<TripItem, 'startsAt' | 'endsAt' | 'confirmation' | 'provider' | 'location'>>;

export async function listTrips(q: { sphere?: string } = {}): Promise<Trip[]> {
  return (await request<{ items: Trip[] }>(`/trips${buildQuery(q)}`)).items;
}
export async function createTrip(body: TripInput): Promise<Trip> {
  return (await request<{ trip: Trip }>('/trips', post(body))).trip;
}
export function getTrip(id: string): Promise<TripDetail> {
  return request(`/trips/${enc(id)}`);
}
export async function updateTrip(id: string, patch: Partial<TripInput>): Promise<Trip> {
  return (await request<{ trip: Trip }>(`/trips/${enc(id)}`, put(patch))).trip;
}
export async function addTripItem(tripId: string, body: TripItemInput): Promise<TripItem> {
  return (await request<{ item: TripItem }>(`/trips/${enc(tripId)}/items`, post(body))).item;
}

// --- Life notes (never /api/notes — that is the medical notes route) -----------------------

export type LifeNoteInput = Pick<LifeNote, 'body'> &
  Partial<Pick<LifeNote, 'title' | 'kind' | 'url' | 'tags' | 'sphereKey' | 'projectId'>>;

export async function listLifeNotes(
  q: { q?: string; kind?: string; tag?: string; sphere?: string } = {},
): Promise<LifeNote[]> {
  return (await request<{ items: LifeNote[] }>(`/life-notes${buildQuery(q)}`)).items;
}
export async function createLifeNote(body: LifeNoteInput): Promise<LifeNote> {
  return (await request<{ note: LifeNote }>('/life-notes', post(body))).note;
}
export async function updateLifeNote(id: string, patch: Partial<LifeNoteInput>): Promise<LifeNote> {
  return (await request<{ note: LifeNote }>(`/life-notes/${enc(id)}`, put(patch))).note;
}
export function deleteLifeNote(id: string, o?: SendOptions): Promise<unknown> {
  return request(`/life-notes/${enc(id)}`, del(o));
}

// --- Timeline, search, upcoming ------------------------------------------------------------

export async function getTimeline(
  q: { from?: string; to?: string; sphere?: string; project?: string; kinds?: string } = {},
): Promise<TimelineItem[]> {
  return (await request<{ items: TimelineItem[] }>(`/timeline${buildQuery(q)}`)).items;
}
export async function searchLife(q: string): Promise<SearchResult[]> {
  return (await request<{ results: SearchResult[] }>(`/search${buildQuery({ q })}`)).results;
}
export async function getUpcoming(days = 30): Promise<UpcomingItem[]> {
  return (await request<{ items: UpcomingItem[] }>(`/upcoming${buildQuery({ days })}`)).items;
}
/** Range mode (the month calendar): everything dated `from`..`to` (YYYY-MM-DD, ≤ 120 days). */
export async function getUpcomingRange(from: string, to: string): Promise<UpcomingItem[]> {
  return (await request<{ items: UpcomingItem[] }>(`/upcoming${buildQuery({ from, to })}`)).items;
}

// --- Today + briefs + proactive settings (bundle C) ------------------------------------------

export function getToday(): Promise<TodayPayload> {
  return request('/today');
}
/** Home's nudge rows use the existing intent route (not a Life OS one) to mark a nudge done. */
export function actNudge(id: string, o?: SendOptions): Promise<unknown> {
  return request(`/nudges/${enc(id)}/act`, post({}, o));
}
export async function listBriefs(q: { kind?: BriefKind; limit?: number } = {}): Promise<BriefListRow[]> {
  return (await request<{ items: BriefListRow[] }>(`/briefs${buildQuery(q)}`)).items;
}
export async function getBrief(id: string): Promise<BriefRow> {
  return (await request<{ brief: BriefRow }>(`/briefs/${enc(id)}`)).brief;
}
export async function generateBrief(kind: BriefKind): Promise<BriefRow> {
  return (await request<{ brief: BriefRow }>(`/briefs/${kind}/generate`, post())).brief;
}
export async function getProactiveSettings(): Promise<ProactiveSettings> {
  return (await request<{ settings: ProactiveSettings }>('/settings/proactive')).settings;
}
export async function updateProactiveSettings(patch: Partial<ProactiveSettings>): Promise<ProactiveSettings> {
  return (await request<{ settings: ProactiveSettings }>('/settings/proactive', put(patch))).settings;
}

// --- Inbox + connectors (bundle D) -------------------------------------------------------------

export function getInbox(
  q: { status?: InboxStatus; sphere?: string; limit?: number } = {},
): Promise<{ items: InboxItem[]; counts: InboxCounts }> {
  return request(`/inbox${buildQuery(q)}`);
}
export async function triageMessage(id: number, body: TriagePatch, o?: SendOptions): Promise<MessageRow> {
  return (await request<{ message: MessageRow }>(`/inbox/${id}/triage`, post(body, o))).message;
}
/** One message by id (search landing when it is not in the loaded view). */
export async function getMessage(id: number): Promise<MessageRow> {
  return (await request<{ message: MessageRow }>(`/inbox/message/${id}`)).message;
}
export async function getThread(threadKey: string): Promise<MessageRow[]> {
  return (await request<{ items: MessageRow[] }>(`/inbox/thread/${enc(threadKey)}`)).items;
}
export function listConnectors(): Promise<ConnectorsPayload> {
  return request('/connectors');
}
/** `{authUrl}`, or ApiError 501 'not configured'. */
export function startConnector(provider: 'gmail' | 'gcal'): Promise<{ authUrl: string }> {
  return request(`/connectors/${provider}/start`, post());
}

// --- Approvals + agents (bundle E) --------------------------------------------------------------

export async function listApprovals(q: { status?: string } = { status: 'pending' }): Promise<ApprovalWithProject[]> {
  return (await request<{ items: ApprovalWithProject[] }>(`/approvals${buildQuery(q)}`)).items;
}
export async function decideApproval(
  id: string,
  decision: ApprovalDecision,
  note?: string,
  o?: SendOptions,
): Promise<ApprovalRow> {
  const body = note ? { decision, note } : { decision };
  return (await request<{ approval: ApprovalRow }>(`/approvals/${enc(id)}/decide`, post(body, o))).approval;
}
export async function listPolicies(): Promise<AgentPolicy[]> {
  return (await request<{ items: AgentPolicy[] }>('/agent/policies')).items;
}
/** 409 when above the project's autonomy ceiling. */
export async function updatePolicy(id: string, level: number, o?: SendOptions): Promise<AgentPolicy> {
  return (await request<{ policy: AgentPolicy }>(`/agent/policies/${enc(id)}`, put({ level }, o))).policy;
}
export async function listRuns(q: { status?: string; project?: string } = {}): Promise<AgentRunListRow[]> {
  return (await request<{ items: AgentRunListRow[] }>(`/agent/runs${buildQuery(q)}`)).items;
}
export async function getRun(id: string): Promise<AgentRunRow> {
  return (await request<{ run: AgentRunRow }>(`/agent/runs/${enc(id)}`)).run;
}
export async function createRun(body: { projectId: string; taskType: string; text: string }): Promise<AgentRunRow> {
  return (await request<{ run: AgentRunRow }>('/agent/runs', post(body))).run;
}
export async function cancelRun(id: string, o?: SendOptions): Promise<AgentRunRow> {
  return (await request<{ run: AgentRunRow }>(`/agent/runs/${enc(id)}/cancel`, post({}, o))).run;
}
export async function listAudit(q: { limit?: number; actor?: string; access?: string } = {}): Promise<AuditRow[]> {
  return (await request<{ items: AuditRow[] }>(`/audit${buildQuery(q)}`)).items;
}

// --- Connections (bundle A, §4.4) -----------------------------------------------------------------

export function getConnections(): Promise<ConnectionsPayload> {
  return request('/connections');
}
/** The plaintext `token` exists only in this response; it is never retrievable again. */
export function createToken(body: CreateTokenInput): Promise<{ token: string; record: TokenRecord }> {
  return request('/connections/tokens', post(body));
}
/** Revokes take effect at once (never deferred): a leaked credential must die now. */
export function revokeToken(id: string): Promise<{ ok: true }> {
  return request(`/connections/tokens/${enc(id)}`, del());
}
export function revokeClient(clientId: string): Promise<{ ok: true }> {
  return request(`/connections/clients/${enc(clientId)}`, del());
}
